import { Injectable, signal, computed, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, debounceTime, filter, finalize, forkJoin, of, switchMap, tap, delay, timer, retry, catchError, EMPTY, throwError } from 'rxjs';
import { ScoreResponse, ScorecardResponse } from '../../../models';
import { ScorecardService } from '../scorecard.service';
import { ConfigService } from '../../config/config.service';
import { LoggingService } from '../../shared/logging/logging.service';
import { ScoringRealtimeService } from '../../signalr/scoring-realtime.service';

export interface BufferedScore {
  score: ScoreResponse;
  scorecard: ScorecardResponse;
  timestamp: number;
  status: 'pending' | 'processing' | 'complete' | 'error' | 'offline';
  retryCount: number;
  key: string;
  isOffline?: boolean;
  lastErrorType?: 'network' | 'server' | 'unknown';
}

export interface BufferState {
  pendingCount: number;
  processingCount: number;
  errorCount: number;
  offlineCount: number;
  lastProcessed: Date | null;
  isProcessing: boolean;
  isOnline: boolean;
  lastConnectivityCheck: Date | null;
}

export interface ScoreBufferConfig {
  batchSize: number;
  bufferTimeMs: number;
  maxRetries: number;
  retryDelayMs: number;
  saveIntervalMs: number;
  flushOnNavigation: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ScoreBufferService implements OnDestroy {
  // Default configuration
  private readonly defaultConfig: ScoreBufferConfig = {
    batchSize: 3,
    bufferTimeMs: 500,    // Reduced from 2000ms for faster testing
    maxRetries: 3,
    retryDelayMs: 500,    // Reduced from 1000ms for faster testing
    saveIntervalMs: 1200, // Delay between serial saves in milliseconds
    flushOnNavigation: true
  };

  // Current configuration
  private config: ScoreBufferConfig;

  // State management
  private state = signal<BufferState>({
    pendingCount: 0,
    processingCount: 0,
    errorCount: 0,
    offlineCount: 0,
    lastProcessed: null,
    isProcessing: false,
    isOnline: navigator.onLine,
    lastConnectivityCheck: new Date()
  });

  // Expose readonly computed signals
  readonly pendingCount = computed(() => this.state().pendingCount);
  readonly processingCount = computed(() => this.state().processingCount);
  readonly errorCount = computed(() => this.state().errorCount);
  readonly offlineCount = computed(() => this.state().offlineCount);
  readonly isProcessing = computed(() => this.state().isProcessing);
  readonly isOnline = computed(() => this.state().isOnline);
  readonly hasErrors = computed(() => this.state().errorCount > 0);
  readonly hasOfflineScores = computed(() => this.state().offlineCount > 0);
  readonly hasPendingChanges = computed(() => this.state().pendingCount > 0 || this.state().processingCount > 0 || this.state().offlineCount > 0);

  // Buffer storage
  private scoreBuffer = new Map<string, BufferedScore>();

  // Track the latest persisted server-assigned scoreId per (scorecardId-holeNumber)
  private persistedScoreIdsByKey = new Map<string, string>();

  // Track local operations for notification suppression
  private localScoreOperations = new Map<string, number[]>();

  // Callback for validation errors to communicate back to components
  private validationErrorCallback?: (scoreId: string) => void;

  // Offline support
  private readonly STORAGE_KEY = 'score-buffer-offline';
  private connectivityCheckInterval?: number;

  // Processing stream
  private processor$ = new Subject<void>();
  private processorSubscription: Subscription;

  constructor(
    private scorecardService: ScorecardService,
    private configService: ConfigService,
    private scoringRealtimeService: ScoringRealtimeService,
    private loggingService: LoggingService
  ) {
    // Initialize configuration
    this.config = {
      ...this.defaultConfig,
      ...this.configService.config?.scoring
    };

    // Load any persisted offline scores
    this.loadOfflineScores();

    // Set up connectivity monitoring
    this.initializeConnectivityMonitoring();

    // Set up processing stream with debounce
    this.processorSubscription = this.processor$.pipe(
      debounceTime(this.config.bufferTimeMs),
      filter(() => !this.isProcessing() && (this.pendingCount() > 0 || (this.isOnline() && this.offlineCount() > 0)))
    ).subscribe(() => this.processBuffer());
  }

  /**
   * Set callback for validation error notifications
   */
  setValidationErrorCallback(callback: (scoreId: string) => void): void {
    this.validationErrorCallback = callback;
  }

  /**
   * Add a score to the buffer for later processing
   */
  bufferScore(score: ScoreResponse, scorecard: ScorecardResponse, hole?: any): void {
    const key = this.getScoreKey(score);

    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Buffering score for hole ${score.holeNumber}, key: ${key}`);
    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Current online status: ${this.isOnline()}`);

    // Track this scorecard ID as a local operation to prevent showing notifications (only if suppression is enabled)
    const suppressLocalOperations = this.configService.config?.notifications?.suppressLocalOperations ?? false;
    if (suppressLocalOperations) {
      const timestamps = this.localScoreOperations.get(score.scorecardId!) || [];
      timestamps.push(Date.now());

      // Keep only the last 10 operations per scorecard to prevent memory issues
      if (timestamps.length > 10) {
        timestamps.splice(0, timestamps.length - 10);
      }

      this.localScoreOperations.set(score.scorecardId!, timestamps);

      // IMPORTANT: Also register with SignalR service for consistent suppression
      this.scoringRealtimeService.registerLocalScorecard(score.scorecardId!);
    }

    // Update the score buffer with the latest state
    const bufferedScore: BufferedScore = {
      score: { ...score },
      scorecard: { ...scorecard },
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
      key,
      isOffline: false
    };

    // Store hole information for plus/minus calculation if provided
    if (hole) {
      (bufferedScore as any).hole = hole;
    }

    // If we're offline, immediately mark as offline instead of pending
    if (!this.isOnline()) {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Currently offline, marking score as offline immediately');
      bufferedScore.status = 'offline';
      bufferedScore.isOffline = true;
      bufferedScore.lastErrorType = 'network';
    }

    this.scoreBuffer.set(key, bufferedScore);
    this.updateBufferCounts();

    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Added score for hole ${score.holeNumber}, buffer size: ${this.scoreBuffer.size}, pending: ${this.pendingCount()}, offline: ${this.offlineCount()}`);

    // Save offline scores to localStorage if offline
    if (!this.isOnline()) {
      this.saveOfflineScores();
    }

    // Trigger processing (will be skipped if offline)
    this.processor$.next();
  }

  /**
   * Check if a specific score is currently being synchronized
   */
  isScoreSyncing(key: string): boolean {
    const bufferedScore = this.scoreBuffer.get(key);
    return bufferedScore?.status === 'processing' || bufferedScore?.status === 'pending';
  }

  /**
   * Check if a specific score key exists in the buffer
   */
  isScoreBuffered(key: string): boolean {
    return this.scoreBuffer.has(key);
  }

  /**
   * Get the status of a specific score
   */
  getScoreStatus(key: string): BufferedScore['status'] | null {
    return this.scoreBuffer.get(key)?.status || null;
  }

  /**
   * Force immediate processing of the buffer
   */
  flushBuffer(): Observable<void> {
    if (this.scoreBuffer.size === 0) {
      return of(void 0);
    }

    // Cancel any pending debounced processing
    this.processor$.next();

    // Process immediately and wait for completion
    return new Observable<void>(observer => {
      const checkCompletion = () => {
        if (this.scoreBuffer.size === 0 && !this.isProcessing()) {
          observer.next();
          observer.complete();
          return;
        }

        // Check again in 100ms
        setTimeout(checkCompletion, 100);
      };

      // Start processing
      this.processBuffer();

      // Start checking for completion
      setTimeout(checkCompletion, 100);

      // Safety timeout to prevent hanging (30 seconds)
      setTimeout(() => {
        observer.next();
        observer.complete();
      }, 30000);
    });
  }

  /**
   * Retry all failed scores
   */
  retryFailedScores(): void {
    const failedScores = Array.from(this.scoreBuffer.values())
      .filter(bufferedScore => bufferedScore.status === 'error');

    failedScores.forEach(bufferedScore => {
      bufferedScore.status = 'pending';
      bufferedScore.retryCount = 0;
      this.scoreBuffer.set(bufferedScore.key, bufferedScore);
    });

    this.updateBufferCounts();
    this.processor$.next();
  }

  /**
   * Clear all buffered scores (use with caution)
   */
  clearBuffer(): void {
    this.scoreBuffer.clear();
    this.updateBufferCounts();
  }

  /**
   * Force immediate processing of the buffer (for testing)
   */
  forceProcessing(): void {
    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Force processing triggered, buffer size: ${this.scoreBuffer.size}`);
    this.processBuffer();
  }

  /**
   * Force sync of offline scores (for testing/manual triggers)
   */
  forceSyncOfflineScores(): void {
    this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Force sync offline scores requested');
    if (this.offlineCount() > 0) {
      this.syncOfflineScores();
    } else {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: No offline scores to sync');
    }
  }

  /**
   * Get current offline status and counts for debugging
   */
  getOfflineStatus() {
    return {
      isOnline: this.isOnline(),
      offlineCount: this.offlineCount(),
      pendingCount: this.pendingCount(),
      processingCount: this.processingCount(),
      offlineScores: Array.from(this.scoreBuffer.values()).filter(s => s.status === 'offline')
    };
  }

  /**
   * Get buffer statistics
   */
  getBufferStats(): BufferState {
    return this.state();
  }

  /**
   * Process the buffer, sending scores to the API in batches
   */
  private processBuffer(): void {
    if (this.isProcessing() || this.scoreBuffer.size === 0) {
      this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Skipping processing - isProcessing: ${this.isProcessing()}, bufferSize: ${this.scoreBuffer.size}`);
      return;
    }

    // Don't process if we're offline (offline scores should stay offline until connectivity returns)
    if (!this.isOnline()) {
      this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Skipping processing - currently offline. Offline scores: ${this.offlineCount()}`);
      return;
    }

    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Starting buffer processing with ${this.scoreBuffer.size} items (online mode)`);
    this.updateState({ isProcessing: true });

    // Get pending scores, sorted by timestamp (oldest first)
    const pendingEntries = Array.from(this.scoreBuffer.entries())
      .filter(([_, entry]) => entry.status === 'pending')
      .sort((a, b) => a[1].timestamp - b[1].timestamp)
      .slice(0, this.config.batchSize);

    if (pendingEntries.length === 0) {
      this.updateState({ isProcessing: false });
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: No pending entries to process');
      return;
    }

    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Processing ${pendingEntries.length} pending entries`);

    // Mark selected entries as processing
    pendingEntries.forEach(([key, entry]) => {
      entry.status = 'processing';
      this.scoreBuffer.set(key, entry);
    });

    this.updateBufferCounts();

    // Create save operations for each score
    const saveOperations = pendingEntries.map(([key, entry]) => {
      return this.saveScoreWithRetry(entry).pipe(
        tap(() => {
          // On success, remove from buffer
          this.scoreBuffer.delete(key);
        }),
        finalize(() => {
          // If still in buffer (due to error), handle retry logic
          if (this.scoreBuffer.has(key)) {
            const updatedEntry = this.scoreBuffer.get(key)!;
            updatedEntry.retryCount++;

            if (updatedEntry.retryCount >= this.config.maxRetries) {
              updatedEntry.status = 'error';
              this.loggingService.debug('ScoreBufferService', `Score save failed after ${this.config.maxRetries} retries:`, updatedEntry.score);
            } else {
              updatedEntry.status = 'pending';
            }

            this.scoreBuffer.set(key, updatedEntry);
          }
        })
      );
    });

    // Execute save operations serially with 1.2 second delays
    this.processSavesSerially(saveOperations, pendingEntries)
      .pipe(
        finalize(() => {
          this.updateState({
            isProcessing: false,
            lastProcessed: new Date()
          });
          this.updateBufferCounts();

          // Save any offline scores to persistent storage
          this.saveOfflineScores();

          // If there are still pending items, trigger processing again
          const hasPending = Array.from(this.scoreBuffer.values())
            .some(entry => entry.status === 'pending');

          if (hasPending) {
            this.loggingService.debug('ScoreBufferService', `ScoreBuffer: ${Array.from(this.scoreBuffer.values()).filter(e => e.status === 'pending').length} items still pending, scheduling retry`);
            setTimeout(() => this.processor$.next(), this.config.retryDelayMs);
          }

          // Log current buffer state
          const offlineCount = Array.from(this.scoreBuffer.values()).filter(e => e.status === 'offline').length;
          if (offlineCount > 0) {
            this.loggingService.debug('ScoreBufferService', `ScoreBuffer: ${offlineCount} scores are offline and will be synced when connectivity returns`);
          }
        })
      )
      .subscribe({
        error: (error: any) => {
          this.loggingService.debug('ScoreBufferService', 'Serial processing error:', error);
        }
      });
  }

  /**
   * Process saves serially with delays between each operation
   */
  private processSavesSerially(saveOperations: Observable<any>[], pendingEntries: [string, BufferedScore][]): Observable<void> {
    if (saveOperations.length === 0) {
      return of(void 0);
    }

    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Processing ${saveOperations.length} saves serially with ${this.config.saveIntervalMs}ms delays`);

    // Process each save operation one at a time with delays
    return new Observable<void>(observer => {
      let currentIndex = 0;

      const processNext = () => {
        if (currentIndex >= saveOperations.length) {
          observer.next();
          observer.complete();
          return;
        }

        const [, entry] = pendingEntries[currentIndex];
        const saveOperation = saveOperations[currentIndex];

        this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Processing save ${currentIndex + 1}/${saveOperations.length} for hole ${entry.score.holeNumber}`);

        saveOperation.subscribe({
          next: () => {
            // Success handled by the operation itself
            this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Save ${currentIndex + 1} completed successfully`);
          },
          error: (error) => {
            this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Save ${currentIndex + 1} failed:`, error);
            // Error handling is done in the save operation itself
          },
          complete: () => {
            currentIndex++;

            if (currentIndex < saveOperations.length) {
              // Wait for the configured interval before processing the next save
              this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Waiting ${this.config.saveIntervalMs}ms before next save...`);
              setTimeout(processNext, this.config.saveIntervalMs);
            } else {
              // All operations completed
              observer.next();
              observer.complete();
            }
          }
        });
      };

      // Start processing
      processNext();
    });
  }

  /**
   * Save a score with retry logic and offline support
   */
  private saveScoreWithRetry(bufferedScore: BufferedScore): Observable<any> {
    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Attempting to save score for hole ${bufferedScore.score.holeNumber}`);

    return this.saveScore(bufferedScore.score, bufferedScore.scorecard).pipe(
      retry({
        count: this.config.maxRetries,
        delay: (error, retryCount) => {
          this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Save attempt ${retryCount} failed:`, error);

          // Check if it's a validation error (400 status) - don't retry these
          if (error.status === 400) {
            this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Validation error detected, stopping retries');
            return throwError(() => error);
          }

          // Check if it's a network error or we're offline
          const isNetworkIssue = this.isNetworkError(error) || !this.isOnline();

          if (isNetworkIssue) {
            this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error detected, stopping retries and going offline');
            // Don't retry network errors - let catchError handle offline logic
            return throwError(() => error);
          }

          // For other errors, use exponential backoff
          const delayMs = this.config.retryDelayMs * Math.pow(2, retryCount - 1);
          this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Retrying save in ${delayMs}ms (attempt ${retryCount}/${this.config.maxRetries})`);
          return timer(delayMs);
        }
      }),
      catchError(error => {
        this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Save failed after retries, error details:', error);

        // Handle validation errors (400 status) - mark as error immediately
        if (error.status === 400) {
          this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Validation error confirmed, marking score as error');
          bufferedScore.status = 'error';
          bufferedScore.lastErrorType = 'server';
          this.scoreBuffer.set(bufferedScore.key, bufferedScore);

          // Notify component about validation error for reversion
          if (this.validationErrorCallback && bufferedScore.score?.scoreId) {
            this.validationErrorCallback(bufferedScore.score.scoreId);
          }

          // Return empty to continue processing other scores without error
          return of(null);
        }

        // Determine error type for other errors
        const isNetworkIssue = this.isNetworkError(error) || !this.isOnline();

        if (isNetworkIssue) {
          this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error confirmed, marking score as offline');

          // Mark as offline and persist
          bufferedScore.status = 'offline';
          bufferedScore.isOffline = true;
          bufferedScore.lastErrorType = 'network';
          this.scoreBuffer.set(bufferedScore.key, bufferedScore);
          this.saveOfflineScores();

          // Update connectivity state if needed
          if (this.isOnline()) {
            this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Updating connectivity state to offline');
            this.handleConnectivityChange(false);
          }

          // Return empty to continue processing other scores without error
          return of(null);
        } else {
          this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Non-network error, letting normal error handling proceed');
          // For non-network errors, let the normal error handling proceed
          return throwError(() => error);
        }
      })
    );
  }

  /**
   * Save a score to the API
   */
  private saveScore(score: ScoreResponse, scorecard: ScorecardResponse): Observable<any> {
    // Register this as a local operation for notification suppression
    this.registerLocalOperation(scorecard.scorecardId!);

    // Resolve key and any known persisted ID for this hole
    const key = this.getScoreKey(score);
    const mappedPersistedId = this.persistedScoreIdsByKey.get(key);

    // If score is empty/null/zero, handle delete semantics
    const isCleared = !score.score?.toString().trim() || score.score === 0;
    if (isCleared) {
      const createdOnUtc = (score as any)?.createdOnUtc;
      // Consider persisted if we either have an explicit mapping or both local ID and createdOnUtc
      const hasPersistedId = !!mappedPersistedId || (!!score.scoreId && !!createdOnUtc);

      if (!hasPersistedId) {
        // Do not call API delete for a non-persisted placeholder row; just no-op
        this.loggingService.debug(
          'ScoreBufferService',
          'Skipping API delete for placeholder/unpersisted score',
          { scoreId: score.scoreId, isNew: score.isNew, createdOnUtc }
        );
        return of(null);
      }

      const deleteId = mappedPersistedId ?? score.scoreId!;
      this.loggingService.debug(
        'ScoreBufferService',
        'Deleting persisted score via API',
        { scoreId: deleteId, scorecardId: scorecard.scorecardId }
      );
      return this.scorecardService
        .deleteScore(scorecard.scorecardId!, deleteId)
        .pipe(
          tap(() => {
            // Clear mapping after successful delete
            this.persistedScoreIdsByKey.delete(key);
          })
        );
    }

    // Calculate plus/minus - try to get hole from the buffered score first
    const bufferedScore = this.scoreBuffer.get(this.getScoreKey(score));
    const hole = (bufferedScore as any)?.hole;
    const plusMinus = hole ?
      this.calculatePlusMinusFromHole(score, hole) :
      this.calculatePlusMinus(score);

    if (score.isNew) {
      this.loggingService.debug('ScoreBufferService', 'Creating score (isNew=true)', { scoreId: score.scoreId, holeNumber: score.holeNumber });
      return this.scorecardService
        .createScoreReturningId({
          ...score,
          plusMinus,
        })
        .pipe(
          tap((newScoreId: string) => {
            // Track the server-assigned ID for this hole for any immediate follow-up operations
            this.persistedScoreIdsByKey.set(key, newScoreId);
            // Best-effort: reflect persistence on the buffered copy to influence any logic relying on createdOnUtc
            score.scoreId = newScoreId;
            (score as any).createdOnUtc = (score as any).createdOnUtc ?? new Date();
            score.isNew = false;
          })
        );
    } else {
      this.loggingService.debug('ScoreBufferService', 'Updating score (isNew=false)', { scoreId: score.scoreId, holeNumber: score.holeNumber });
      return this.scorecardService.updateScore({
        ...score,
        plusMinus,
      });
    }
  }

  /**
   * Calculate plus/minus using hole information
   */
  private calculatePlusMinusFromHole(score: ScoreResponse, hole: any): number {
    return (score.score || 0) - (hole.par || 4);
  }

  /**
   * Calculate plus/minus for a score (placeholder implementation)
   * You may need to inject hole data or pass it differently
   */
  private calculatePlusMinus(score: ScoreResponse): number {
    // This is a simplified implementation
    // In practice, you'd need access to hole par information
    const defaultPar = 4; // Default fallback
    return (score.score || 0) - defaultPar;
  }

  /**
   * Generate a unique key for a score
   */
  private getScoreKey(score: ScoreResponse): string {
    return `${score.scorecardId}-${score.holeNumber}`;
  }

  /**
   * Update buffer counts in the state
   */
  private updateBufferCounts(): void {
    const scores = Array.from(this.scoreBuffer.values());

    const pendingCount = scores.filter(s => s.status === 'pending').length;
    const processingCount = scores.filter(s => s.status === 'processing').length;
    const errorCount = scores.filter(s => s.status === 'error').length;
    const offlineCount = scores.filter(s => s.status === 'offline').length;

    this.updateState({
      pendingCount,
      processingCount,
      errorCount,
      offlineCount
    });
  }

  /**
   * Initialize connectivity monitoring
   */
  private initializeConnectivityMonitoring(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleConnectivityChange(true));
    window.addEventListener('offline', () => this.handleConnectivityChange(false));

    // Periodic connectivity check (every 30 seconds when offline)
    this.connectivityCheckInterval = window.setInterval(() => {
      if (!this.isOnline()) {
        this.checkConnectivity();
      }
    }, 30000);

    this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Connectivity monitoring initialized');
  }

  /**
   * Handle connectivity status changes
   */
  private handleConnectivityChange(isOnline: boolean): void {
    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Connectivity changed - ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: navigator.onLine = ${navigator.onLine}`);

    this.updateState({
      isOnline,
      lastConnectivityCheck: new Date()
    });

    if (isOnline && this.offlineCount() > 0) {
      this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Connection restored, attempting to sync ${this.offlineCount()} offline scores`);
      // Add a small delay to ensure UI updates before sync starts
      setTimeout(() => {
        this.syncOfflineScores();
      }, 100);
    } else if (!isOnline) {
      this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Now offline, future scores will be stored locally`);
    }
  }

  /**
   * Check connectivity by attempting a lightweight API call
   */
  private checkConnectivity(): void {
    // Simple connectivity test - try to reach the API
    fetch(this.configService.config?.apiBasePath + '/health', {
      method: 'HEAD',
      cache: 'no-cache'
    })
      .then(() => {
        if (!this.isOnline()) {
          this.handleConnectivityChange(true);
        }
      })
      .catch(() => {
        // Still offline, no action needed
      });
  }

  /**
   * Load offline scores from localStorage
   */
  private loadOfflineScores(): void {
    try {
      const storedData = localStorage.getItem(this.STORAGE_KEY);
      if (storedData) {
        const offlineScores: BufferedScore[] = JSON.parse(storedData);

        offlineScores.forEach(score => {
          score.status = 'offline';
          this.scoreBuffer.set(score.key, score);
        });

        this.updateBufferCounts();
        this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Loaded ${offlineScores.length} offline scores from storage`);
      }
    } catch (error) {
      this.loggingService.error('ScoreBufferService', 'ScoreBuffer: Failed to load offline scores:', error);
    }
  }

  /**
   * Save offline scores to localStorage
   */
  private saveOfflineScores(): void {
    try {
      const offlineScores = Array.from(this.scoreBuffer.values())
        .filter(score => score.status === 'offline');

      if (offlineScores.length > 0) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(offlineScores));
        this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Saved ${offlineScores.length} offline scores to storage`);
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      this.loggingService.error('ScoreBufferService', 'ScoreBuffer: Failed to save offline scores:', error);
    }
  }

  /**
   * Sync offline scores when connectivity is restored
   */
  private syncOfflineScores(): void {
    const offlineScores = Array.from(this.scoreBuffer.values())
      .filter(score => score.status === 'offline');

    if (offlineScores.length === 0) return;

    this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Starting sync of ${offlineScores.length} offline scores`);

    // Mark offline scores as pending to trigger normal processing
    offlineScores.forEach(score => {
      score.status = 'pending';
      score.retryCount = 0; // Reset retry count for offline scores
      this.scoreBuffer.set(score.key, score);
    });

    this.updateBufferCounts();
    this.processor$.next(); // Trigger immediate processing
  }

  /**
   * Determine if an error is a network connectivity issue
   */
  private isNetworkError(error: any): boolean {
    if (!error) return false;

    this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Analyzing error for network issues:', error);

    // Check for common network error indicators
    if (error.status === 0) {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error detected - status 0 (no response)');
      return true;
    }

    if (error.status >= 500 && error.status < 600) {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Server error detected - not treating as network issue');
      return false; // Server errors are not network issues
    }

    if (error.name === 'TimeoutError') {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error detected - timeout');
      return true;
    }

    if (error.message?.includes('NetworkError')) {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error detected - NetworkError in message');
      return true;
    }

    if (error.message?.includes('Failed to fetch')) {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error detected - Failed to fetch');
      return true;
    }

    // Chrome Dev Tools offline mode often returns these
    if (error.message?.includes('net::ERR_INTERNET_DISCONNECTED')) {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error detected - Chrome offline mode');
      return true;
    }

    if (error.message?.includes('ERR_NETWORK_CHANGED')) {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error detected - Network changed');
      return true;
    }

    // Additional Angular HTTP error checks
    if (error.error && error.error.message?.includes('Http failure')) {
      this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Network error detected - Angular HTTP failure');
      return true;
    }

    this.loggingService.debug('ScoreBufferService', 'ScoreBuffer: Not identified as network error');
    return false;
  }

  /**
   * Register a local operation for notification suppression
   */
  private registerLocalOperation(scorecardId: string): void {
    const suppressLocalOperations = this.configService.config?.notifications?.suppressLocalOperations ?? false;

    if (suppressLocalOperations) {
      // Register with local tracking (for scorecard component compatibility)
      const timestamps = this.localScoreOperations.get(scorecardId) || [];
      timestamps.push(Date.now());

      // Keep only the last 10 operations per scorecard to prevent memory issues
      if (timestamps.length > 10) {
        timestamps.splice(0, timestamps.length - 10);
      }

      this.localScoreOperations.set(scorecardId, timestamps);

      // Also register with the realtime service for SignalR notification suppression
      this.scoringRealtimeService.registerLocalScorecard(scorecardId);

      this.loggingService.debug('ScoreBufferService', `ScoreBuffer: Registered local operation for scorecard ${scorecardId}`);
    }
  }

  /**
   * Update the state signal
   */
  private updateState(changes: Partial<BufferState>): void {
    this.state.update(current => ({
      ...current,
      ...changes
    }));
  }

  /**
   * Clean up resources
   */
  ngOnDestroy(): void {
    if (this.processorSubscription) {
      this.processorSubscription.unsubscribe();
    }

    if (this.connectivityCheckInterval) {
      clearInterval(this.connectivityCheckInterval);
    }

    // Save any remaining offline scores before cleanup
    this.saveOfflineScores();

    // Remove event listeners
    window.removeEventListener('online', () => this.handleConnectivityChange(true));
    window.removeEventListener('offline', () => this.handleConnectivityChange(false));
  }
}
